import React from 'react';
import { motion } from 'framer-motion';

const servicesData = [
  {
    id: 1,
    title: "Powered by ChatGPT",
    description: "The cutting-edge language model that makes interactions a breeze. Effortlessly tap into the world of AI-generated text.",
    icon: "https://www.svgrepo.com/show/530438/ddos-protection.svg",
    link: null
  },
  {
    id: 2,
    title: "Easy to use",
    description: "Simply input your subject, click the generate button, and the result will appear in seconds like magic.",
    icon: "https://www.svgrepo.com/show/530442/port-detection.svg",
    link: null
  },
  {
    id: 3,
    title: "Custom settings",
    description: "We offer advanced customization. Combine roles, languages, tones, lengths, and formats freely.",
    icon: "https://www.svgrepo.com/show/530444/availability.svg",
    link: null
  },
  {
    id: 4,
    title: "Free trial",
    description: "We offer a free trial without login. Multiple payment options are available.",
    icon: "https://www.svgrepo.com/show/530440/machine-vision.svg",
    link: "/pricing"
  },
  {
    id: 5,
    title: "90+ templates",
    description: "Templates for writing, education, lifestyle and creativity.",
    icon: "https://www.svgrepo.com/show/530450/page-analysis.svg",
    link: "/templates",
    mobileHide: true 
  },
  {
    id: 6,
    title: "Use Anywhere",
    description: "Compatible with Web, Chrome, Windows and Mac. Use it anywhere.",
    icon: "https://www.svgrepo.com/show/530453/mail-reception.svg",
    link: "/download",
    mobileHide: true
  },
  {
    id: 7,
    title: "Cloud Storage",
    description: "Securely store your documents and access them from anywhere in the world with our encrypted cloud solution.",
    icon: "https://www.svgrepo.com/show/530457/cloud-storage.svg",
    link: "/cloud",
    mobileHide: true
  },
  {
    id: 8,
    title: "API Access",
    description: "Developers can integrate our powerful API into their own applications seamlessly.",
    icon: "https://www.svgrepo.com/show/530460/code-api.svg",
    link: "/developers",
    mobileHide: true
  }
];

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  }
};

const ServicesSection = () => {
  return (
    <div className="bg-gray-50 px-4 py-10 lg:pt-14 sm:px-6 lg:px-0 overflow-hidden">
      <div id="features" className="mx-auto max-w-[90vw]">

       {/* Heading */}
<motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.5, ease: "easeOut" }}
  className="text-center mb-8 lg:mb-14"
>
  <h2 className="text-center text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-[var(--bg-primary)] drop-shadow-sm mb-2 lg:mb-2">
    Our Exceptional Services
  </h2>
  <div className="w-40 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-2"></div>
  <p className="text-sm md:text-sm text-gray-500 text-center max-w-md mx-auto">
    Delivering quality solutions with care and expertise. Your success is our priority.
  </p>
</motion.div>



        {/* Services Grid */}
        <motion.ul
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 text-center text-slate-700"
          initial="hidden"
          animate="visible"
        >
          {servicesData.map((service, index) => (
            <motion.li
              key={service.id}
              variants={cardVariants}
              whileHover={{
                y: -10,
                scale: 1.03,
                rotateX: 6,
                rotateY: -6,
                boxShadow: "0px 20px 40px rgba(59,130,246,0.25)"
              }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className={`${service.mobileHide ? 'hidden lg:block' : ''} relative group rounded-2xl bg-white px-4 lg:px-6 py-4 lg:py-8 shadow-md border border-gray-100 overflow-hidden`}
            >
              {/* Glow Layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {service.link ? (
                <a href={service.link} className="relative h-full flex flex-col items-center z-10">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ rotate: 15, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="lg:w-14 w-10 lg:h-14 h-10 mx-auto rounded-full bg-[var(--bg-primary)] flex items-center justify-center shadow-lg mb-1 lg:mb-4"
                  >
                    <img
                      src={service.icon}
                      alt={service.title}
                      className="lg:h-7 h:5 lg:w-7 w-5 brightness-0 invert"
                    />
                  </motion.div>

                  <h3 className="my-1 lg:my-2 lg:text-lg font-bold text-gray-900 transition-all duration-300 group-hover:translate-y-[-2px] group-hover:text-[var(--text-primary)]">
                    {service.title}
                  </h3>

                  <p className="lg:mt-2 text-xs leading-relaxed text-gray-500 transition-all duration-300 group-hover:text-gray-700">
                    {service.description}
                  </p>
                </a>
              ) : (
                <div className="relative h-full flex flex-col items-center z-10">
                   {/* Icon */}
                  <motion.div
                    whileHover={{ rotate: 15, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="lg:w-14 w-10 lg:h-14 h-10 mx-auto rounded-full bg-[var(--bg-primary)] flex items-center justify-center shadow-lg mb-1 lg:mb-4"
                  >
                    <img
                      src={service.icon}
                      alt={service.title}
                      className="lg:h-7 h:5 lg:w-7 w-5 brightness-0 invert"
                    />
                  </motion.div>

                  <h3 className="my-1 lg:my-2 lg:text-lg font-bold text-gray-900 transition-all duration-300 group-hover:translate-y-[-2px] group-hover:text-[var(--text-primary)]">
                    {service.title}
                  </h3>

                  <p className="lg:mt-2 text-xs leading-relaxed text-gray-500 transition-all duration-300 group-hover:text-gray-700">
                    {service.description}
                  </p>
                </div>
              )}
            </motion.li>
          ))}
        </motion.ul>

      </div>
    </div>
  );
};

export default ServicesSection;
